import React from 'react';
import { DataTable, EmptyValue, RowActions, IdLink, type DataTableColumn } from '@/components/ui';
import { ServiceArea } from '../../../services/areasService';
import { useClientSort } from '../../../utils/useClientSort';
import { countLabel } from '../utils/areaFilters';
import { AreaCountLink } from './AreaRelationLink';

interface ServiceAreasTableProps {
  serviceAreas: ServiceArea[];
  onEdit: (serviceArea: ServiceArea) => void;
  onDelete: (id: number) => void;
  canEdit: boolean;
  canDelete: boolean;
  /** Open the Foreman Areas tab filtered to this service area (W-M22). */
  onShowForemanAreas?: (serviceArea: ServiceArea) => void;
  emptyMessage?: string;
}

const SORT_VALUES: Record<string, (sa: ServiceArea) => string | number | null | undefined> = {
  name: (sa) => sa.name,
  code: (sa) => sa.code,
  foremanAreas: (sa) => sa.foremanAreas?.length || 0,
};

const ServiceAreasTable: React.FC<ServiceAreasTableProps> = ({
  serviceAreas,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  onShowForemanAreas,
  emptyMessage = 'No service areas found.',
}) => {
  const { sorted, sortBy, sortDir, onSort } = useClientSort(serviceAreas, SORT_VALUES);

  const columns: DataTableColumn<ServiceArea>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      mobile: 'title',
      // V3-025 / ACT-01: the name is the link that opens the record (when the user can edit it).
      cell: (sa) => (canEdit ? <IdLink onClick={() => onEdit(sa)}>{sa.name}</IdLink> : <strong>{sa.name}</strong>),
    },
    {
      key: 'code',
      header: 'Code',
      sortable: true,
      mobileLabel: 'Code',
      cell: (sa) =>
        // V2-032: Code is plain text, not a pill (STAT-02).
        sa.code ? sa.code : <EmptyValue />,
    },
    {
      key: 'foremanAreas',
      header: 'Foreman Areas',
      sortable: true,
      mobileLabel: 'Foreman Areas',
      cell: (sa) => {
        const n = sa.foremanAreas?.length || 0;
        return (
          <AreaCountLink
            count={n}
            label={countLabel(n, 'foreman area')}
            onOpen={() => onShowForemanAreas?.(sa)}
            title={`Show the foreman areas in ${sa.name}`}
          />
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'trailing',
      hidden: !(canEdit || canDelete),
      cell: (sa) => {
        const hasChildren = !!sa.foremanAreas && sa.foremanAreas.length > 0;
        return (
          <RowActions
            actions={[
              {
                kind: 'edit',
                label: 'Edit Service Area',
                onClick: () => onEdit(sa),
                hidden: !canEdit,
              },
              {
                kind: 'delete',
                // The disabled reason rides in the label — a kebab item has no hover target.
                label: hasChildren ? 'Delete Service Area – has foreman areas' : 'Delete Service Area',
                onClick: () => onDelete(sa.id),
                disabled: hasChildren,
                hidden: !canDelete,
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={sorted}
      rowKey={(sa) => sa.id}
      striped
      bordered
      hover
      sortBy={sortBy}
      sortDir={sortDir}
      onSort={onSort}
      emptyMessage={emptyMessage}
    />
  );
};

export default ServiceAreasTable;
